"use client";

import { useState, type ReactNode } from "react";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { useServerInsertedHTML } from "next/navigation";
import { buildServerStyleTags, isGlobalInsert, type InsertedStyle } from "./serverStyleTags";

/**
 * Collects Chakra's styles while the server renders and writes them into the head, so the first
 * client render matches. @chakra-ui/next-js's CacheProvider isn't used because Chakra's components
 * didn't see its cache on the server, and every style rendered inline in the body instead.
 */
export default function EmotionCacheProvider({ children }: { children: ReactNode }) {
  const [{ cache, takeInserted }] = useState(() => {
    const cache = createCache({ key: "css", prepend: true });
    // Tells emotion to collect styles in the cache rather than render them beside each element.
    cache.compat = true;

    let pending: InsertedStyle[] = [];
    const insert = cache.insert;
    cache.insert = (...args) => {
      const [selector, serialized] = args;
      if (cache.inserted[serialized.name] === undefined) {
        pending.push({
          name: serialized.name,
          isGlobal: isGlobalInsert(selector, serialized.styles),
        });
      }
      return insert(...args);
    };
    const takeInserted = () => {
      const styles = pending;
      pending = [];
      return styles;
    };
    return { cache, takeInserted };
  });

  // Runs once per streamed chunk, so each chunk carries only the styles it introduced.
  useServerInsertedHTML(() => {
    const tags = buildServerStyleTags(cache.key, takeInserted(), cache.inserted);
    if (tags.length === 0) {
      return null;
    }
    return tags.map((tag) => (
      <style
        key={tag.dataEmotion}
        data-emotion={tag.dataEmotion}
        dangerouslySetInnerHTML={{ __html: tag.css }}
      />
    ));
  });

  return <CacheProvider value={cache}>{children}</CacheProvider>;
}
